import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "pwa-install-dismissed";

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(() =>
    localStorage.getItem(DISMISSED_KEY) === "true"
  );

  // Detect iOS Safari (doesn't support beforeinstallprompt)
  const isIOS =
    /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
  const isInStandaloneMode =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true;

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const canInstall =
    !isDismissed &&
    !isInStandaloneMode &&
    (deferredPrompt !== null || isIOS);

  const showIOSInstructions = !isDismissed && !isInStandaloneMode && isIOS;

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, "true");
    setIsDismissed(true);
  }

  return { canInstall, showIOSInstructions, install, dismiss };
}
